const REFERRAL_PARAMS_STORAGE_KEY = "referral_params";
const REFERRAL_PARTNER_ID_STORAGE_KEY = "referral_partner_id";
const ALLOWED_REFERRAL_KEYS = new Set(["partner_id"]);

const decodeBase64Query = (value: string): string | null => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    "="
  );

  try {
    const binary = window.atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
};

const toRecord = (params: URLSearchParams) => {
  return Array.from(params.entries()).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (key && value) {
        acc[key] = value;
      }

      return acc;
    },
    {}
  );
};

const filterReferralParams = (params: URLSearchParams) => {
  const filteredParams = new URLSearchParams();

  params.forEach((value, key) => {
    if (ALLOWED_REFERRAL_KEYS.has(key) && value) {
      filteredParams.set(key, value);
    }
  });

  return filteredParams;
};

export const extractReferralParamsFromSearch = (
  search: string
): URLSearchParams | null => {
  const trimmed = search.replace(/^\?/, "").trim();

  if (!trimmed) {
    return null;
  }

  const rawQuery = /[=&]/.test(trimmed)
    ? trimmed
    : decodeBase64Query(trimmed) ?? "";

  if (!rawQuery) {
    return null;
  }

  const params = filterReferralParams(new URLSearchParams(rawQuery));

  return Array.from(params.keys()).length > 0 ? params : null;
};

export const persistReferralParamsFromSearch = (search: string) => {
  const params = extractReferralParamsFromSearch(search);

  if (!params) {
    return null;
  }

  const serialized = params.toString();
  const partnerId = params.get("partner_id");

  localStorage.setItem(REFERRAL_PARAMS_STORAGE_KEY, serialized);

  if (partnerId) {
    localStorage.setItem(REFERRAL_PARTNER_ID_STORAGE_KEY, partnerId);
  } else {
    localStorage.removeItem(REFERRAL_PARTNER_ID_STORAGE_KEY);
  }

  return {
    serialized,
    partnerId,
    params,
  };
};

export const getStoredReferralParams = () => {
  const stored = localStorage.getItem(REFERRAL_PARAMS_STORAGE_KEY) ?? "";
  return filterReferralParams(new URLSearchParams(stored));
};

export const getStoredReferralPayload = () => {
  return toRecord(getStoredReferralParams());
};

export const appendStoredReferralParams = (params: URLSearchParams) => {
  const storedParams = getStoredReferralParams();

  storedParams.forEach((value, key) => {
    if (key && value && !params.has(key)) {
      params.set(key, value);
    }
  });

  return params;
};

export const getStoredPartnerId = () => {
  return localStorage.getItem(REFERRAL_PARTNER_ID_STORAGE_KEY);
};
